'use client'

import { forwardRef, useState, useCallback, useMemo, useEffect } from 'react'
import { Check, Minus, Users, Shield, Calendar } from 'lucide-react'
import { safeAccess } from '../utils/security'

/**
 * BIRHAUS Checkbox Group Component
 * 
 * Enhanced checkbox group implementing all 10 BIRHAUS principles:
 * - Spanish-first labels and descriptions
 * - Progressive disclosure with grouping and categorization
 * - Cognitive load reduction (max 7 items per group)
 * - Accessibility compliance (WCAG AA+)
 * - Select all/none functionality
 * - IPU Admin specific presets for permissions and ministries
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export interface CheckboxOption {
  id: string
  value: string
  labelEs: string
  labelEn: string
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  disabled?: boolean
  icon?: React.ReactNode
  category?: string
}

export interface CheckboxCategory {
  id: string
  labelEs: string
  labelEn: string
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  icon?: React.ReactNode
  color?: string
}

export interface BirhausCheckboxGroupProps {
  // BIRHAUS Principle #7: Bilingual by design
  label?: string
  labelEs?: string
  labelEn?: string
  
  // BIRHAUS Principle #4: Progressive disclosure
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  
  // Options and values
  options: CheckboxOption[]
  value?: string[]
  defaultValue?: string[]
  onChange?: (values: string[]) => void
  
  // BIRHAUS Principle #2: Honest data
  error?: string
  errorEs?: string
  errorEn?: string
  
  // Grouping and categorization
  categories?: CheckboxCategory[]
  showCategories?: boolean
  maxItemsPerCategory?: number // Default 7 (Miller's Law)
  
  // BIRHAUS Principle #6: Accessibility = dignity
  disabled?: boolean
  required?: boolean
  
  // Select all functionality
  showSelectAll?: boolean
  selectAllLabelEs?: string
  selectAllLabelEn?: string
  
  // Appearance
  variant?: 'default' | 'cards' | 'minimal'
  size?: 'sm' | 'md' | 'lg'
  layout?: 'vertical' | 'horizontal' | 'grid'
  
  // Validation
  minSelections?: number
  maxSelections?: number
  
  // Locale
  locale?: 'es' | 'en'
  
  // IPU Admin specific
  presetType?: 'permissions' | 'ministries' | 'roles' | 'custom'
}

export const BirhausCheckboxGroup = forwardRef<HTMLDivElement, BirhausCheckboxGroupProps>(({
  // Labels (Spanish-first)
  label,
  labelEs,
  labelEn,
  
  // Description
  description,
  descriptionEs,
  descriptionEn,
  
  // Options
  options,
  value,
  defaultValue = [],
  onChange,
  
  // Validation
  error,
  errorEs,
  errorEn,
  
  // Grouping
  categories,
  showCategories = true,
  maxItemsPerCategory = 7,
  
  // Basic props
  disabled = false,
  required = false,
  
  // Select all
  showSelectAll = true,
  selectAllLabelEs = 'Seleccionar todos',
  selectAllLabelEn = 'Select all',
  
  // Appearance
  variant = 'default',
  size = 'md',
  layout = 'vertical',
  
  // Validation
  minSelections,
  maxSelections,
  
  // Locale
  locale = 'es',
  
  // IPU Admin
  presetType = 'custom'
}, ref) => {
  // State management
  const [internalValue, setInternalValue] = useState<string[]>(value || defaultValue)
  const [validationError, setValidationError] = useState<string>('')
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const getDisplayText = useCallback((
    primary: string | undefined,
    spanish: string | undefined,
    english: string | undefined,
    fallback: string
  ): string => {
    if (locale === 'es') {
      return primary || spanish || english || fallback
    }
    return primary || english || spanish || fallback
  }, [locale])
  
  const displayLabel = getDisplayText(label, labelEs, labelEn, '')
  const displayDescription = getDisplayText(description, descriptionEs, descriptionEn, '')
  const displayError = error || errorEs || validationError
  const selectAllLabel = getDisplayText(undefined, selectAllLabelEs, selectAllLabelEn, 'Select all')
  
  // Get default categories for IPU Admin
  const getDefaultCategories = useCallback((): CheckboxCategory[] => {
    const categoryConfigs: Record<string, CheckboxCategory[]> = {
      permissions: [
        {
          id: 'admin',
          labelEs: 'Administración',
          labelEn: 'Administration',
          descriptionEs: 'Permisos de administración del sistema',
          descriptionEn: 'System administration permissions',
          icon: <Shield className="h-4 w-4" />,
          color: 'red'
        },
        {
          id: 'financial',
          labelEs: 'Financiero',
          labelEn: 'Financial',
          descriptionEs: 'Gestión de donaciones y finanzas',
          descriptionEn: 'Donations and financial management',
          icon: <Users className="h-4 w-4" />,
          color: 'green'
        },
        {
          id: 'events',
          labelEs: 'Eventos',
          labelEn: 'Events',
          descriptionEs: 'Organización y gestión de eventos',
          descriptionEn: 'Event organization and management',
          icon: <Calendar className="h-4 w-4" />,
          color: 'blue'
        }
      ],
      ministries: [
        {
          id: 'worship',
          labelEs: 'Adoración',
          labelEn: 'Worship',
          descriptionEs: 'Ministerios de música y adoración',
          descriptionEn: 'Music and worship ministries',
          color: 'purple'
        },
        {
          id: 'children',
          labelEs: 'Niños',
          labelEn: 'Children',
          descriptionEs: 'Ministerios para niños y familias',
          descriptionEn: 'Children and family ministries',
          color: 'yellow'
        },
        {
          id: 'youth',
          labelEs: 'Jóvenes',
          labelEn: 'Youth',
          descriptionEs: 'Ministerios juveniles',
          descriptionEn: 'Youth ministries',
          color: 'indigo'
        }
      ],
      roles: [
        {
          id: 'leadership',
          labelEs: 'Liderazgo',
          labelEn: 'Leadership',
          descriptionEs: 'Roles de liderazgo y dirección',
          descriptionEn: 'Leadership and direction roles',
          color: 'orange'
        },
        {
          id: 'service',
          labelEs: 'Servicio',
          labelEn: 'Service',
          descriptionEs: 'Roles de servicio y apoyo',
          descriptionEn: 'Service and support roles',
          color: 'teal'
        }
      ]
    }
    
    return safeAccess(categoryConfigs, presetType, [])
  }, [presetType])
  
  const allCategories = categories || getDefaultCategories()
  
  // Group options by category
  const groupedOptions = useMemo(() => {
    if (!showCategories || allCategories.length === 0) {
      return { uncategorized: options }
    }
    
    const grouped: Record<string, CheckboxOption[]> = {}
    const uncategorized: CheckboxOption[] = []
    
    // Initialize category groups
    allCategories.forEach(category => {
      grouped[category.id] = []
    })
    
    // Group options
    options.forEach(option => {
      if (option.category && grouped[option.category]) {
        // Limit items per category (Miller's Law)
        if (grouped[option.category].length < maxItemsPerCategory) {
          grouped[option.category].push(option)
        }
      } else {
        uncategorized.push(option)
      }
    })
    
    // Add uncategorized if any
    if (uncategorized.length > 0) {
      grouped.uncategorized = uncategorized
    }
    
    return grouped
  }, [options, allCategories, showCategories, maxItemsPerCategory])
  
  // Validation function
  const validateSelection = useCallback((values: string[]): string => {
    if (required && values.length === 0) {
      return locale === 'es'
        ? 'Debe seleccionar al menos una opción'
        : 'Must select at least one option'
    }
    
    if (minSelections && values.length < minSelections) {
      return locale === 'es'
        ? `Debe seleccionar al menos ${minSelections} opciones`
        : `Must select at least ${minSelections} options`
    }
    
    if (maxSelections && values.length > maxSelections) {
      return locale === 'es'
        ? `Puede seleccionar máximo ${maxSelections} opciones`
        : `Can select maximum ${maxSelections} options`
    }
    
    return ''
  }, [required, minSelections, maxSelections, locale])
  
  // Handle option change
  const handleOptionChange = useCallback((optionValue: string, checked: boolean) => {
    if (disabled) return
    
    let newValues: string[]
    
    if (checked) {
      // Check max selections before adding
      if (maxSelections && internalValue.length >= maxSelections) {
        setValidationError(
          locale === 'es'
            ? `Máximo ${maxSelections} opciones permitidas`
            : `Maximum ${maxSelections} options allowed`
        )
        return
      }
      newValues = [...internalValue, optionValue]
    } else {
      newValues = internalValue.filter(v => v !== optionValue)
    }
    
    setInternalValue(newValues)
    
    // Validate
    const errorMessage = validateSelection(newValues)
    setValidationError(errorMessage)
    
    // Call parent onChange
    onChange?.(newValues)
  }, [internalValue, onChange, disabled, maxSelections, validateSelection, locale])
  
  // Handle select all for category
  const handleSelectAllCategory = useCallback((categoryId: string, options: CheckboxOption[]) => {
    if (disabled) return
    
    const categoryValues = options.filter(opt => !opt.disabled).map(opt => opt.value)
    const areAllSelected = categoryValues.every(val => internalValue.includes(val))
    
    let newValues: string[]
    
    if (areAllSelected) {
      // Deselect all in category
      newValues = internalValue.filter(val => !categoryValues.includes(val))
    } else {
      // Select all in category (respect max selections)
      const availableSlots = maxSelections ? maxSelections - internalValue.length : Infinity
      const toAdd = categoryValues
        .filter(val => !internalValue.includes(val))
        .slice(0, availableSlots)
      
      newValues = [...internalValue, ...toAdd]
      
      if (maxSelections && toAdd.length < categoryValues.filter(val => !internalValue.includes(val)).length) {
        setValidationError(
          locale === 'es'
            ? `Máximo ${maxSelections} opciones permitidas`
            : `Maximum ${maxSelections} options allowed`
        )
      }
    }
    
    setInternalValue(newValues)
    
    // Validate
    const errorMessage = validateSelection(newValues)
    if (!maxSelections || newValues.length <= maxSelections) {
      setValidationError(errorMessage)
    }
    
    onChange?.(newValues)
  }, [internalValue, onChange, disabled, maxSelections, validateSelection, locale])
  
  // Handle select all (global)
  const handleSelectAll = useCallback(() => {
    if (disabled) return
    
    const allValues = options.filter(opt => !opt.disabled).map(opt => opt.value)
    const areAllSelected = allValues.every(val => internalValue.includes(val))
    
    let newValues: string[]
    
    if (areAllSelected) {
      newValues = []
    } else {
      // Respect max selections
      newValues = maxSelections ? allValues.slice(0, maxSelections) : allValues
    }
    
    setInternalValue(newValues)
    
    // Validate
    const errorMessage = validateSelection(newValues)
    setValidationError(errorMessage)
    
    onChange?.(newValues)
  }, [options, internalValue, onChange, disabled, maxSelections, validateSelection])
  
  // Get category selection state
  const getCategoryState = useCallback((categoryOptions: CheckboxOption[]) => {
    const enabledOptions = categoryOptions.filter(opt => !opt.disabled)
    const selectedCount = enabledOptions.filter(opt => internalValue.includes(opt.value)).length
    
    if (selectedCount === 0) return 'none'
    if (selectedCount === enabledOptions.length) return 'all'
    return 'partial'
  }, [internalValue])
  
  // Sync external value changes
  useEffect(() => {
    if (value && value !== internalValue) {
      setInternalValue(value)
    }
  }, [value, internalValue])
  
  // Style classes
  const sizeClasses = {
    sm: {
      checkbox: 'h-4 w-4',
      text: 'text-sm',
      description: 'text-xs'
    },
    md: {
      checkbox: 'h-5 w-5',
      text: 'text-base',
      description: 'text-sm'
    },
    lg: {
      checkbox: 'h-6 w-6',
      text: 'text-lg',
      description: 'text-base'
    }
  }
  
  const variantClasses = {
    default: {
      container: 'space-y-3',
      group: 'border border-gray-200 rounded-lg p-4',
      option: 'flex items-start space-x-3'
    },
    cards: {
      container: 'grid gap-3',
      group: 'border border-gray-200 rounded-lg p-4 bg-gray-50',
      option: 'flex items-start space-x-3 p-3 border border-gray-200 rounded-md bg-white hover:bg-gray-50'
    },
    minimal: {
      container: 'space-y-2',
      group: 'space-y-2',
      option: 'flex items-start space-x-3'
    }
  }
  
  const layoutClasses = {
    vertical: 'space-y-3',
    horizontal: 'flex flex-wrap gap-6',
    grid: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'
  }
  
  const currentSizeClasses = safeAccess(sizeClasses, size, sizeClasses.md)
  const currentVariantClasses = safeAccess(variantClasses, variant, variantClasses.default)
  
  // Render individual checkbox
  const renderCheckbox = useCallback((option: CheckboxOption) => {
    const isChecked = internalValue.includes(option.value)
    const isDisabled = disabled || option.disabled
    
    const optionLabel = getDisplayText(
      undefined,
      option.labelEs,
      option.labelEn,
      option.value
    )
    
    const optionDescription = getDisplayText(
      option.description,
      option.descriptionEs,
      option.descriptionEn,
      ''
    )
    
    return (
      <div
        key={option.id}
        className={cn(
          currentVariantClasses.option,
          isDisabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <div className="relative">
          <input
            type="checkbox"
            id={option.id}
            checked={isChecked}
            onChange={(e) => handleOptionChange(option.value, e.target.checked)}
            disabled={isDisabled}
            className="sr-only"
          />
          <div
            className={cn(
              'border-2 rounded transition-colors duration-200 flex items-center justify-center',
              currentSizeClasses.checkbox,
              isChecked
                ? 'bg-primary-600 border-primary-600 text-white'
                : 'border-gray-300 bg-white',
              !isDisabled && 'cursor-pointer hover:border-primary-400',
              isDisabled && 'cursor-not-allowed'
            )}
            onClick={() => !isDisabled && handleOptionChange(option.value, !isChecked)}
          >
            {isChecked && <Check className="h-3 w-3" />}
          </div>
        </div>
        
        <div className="flex-1">
          <label
            htmlFor={option.id}
            className={cn(
              'font-medium cursor-pointer',
              currentSizeClasses.text,
              isDisabled ? 'cursor-not-allowed text-gray-400' : 'text-gray-900'
            )}
          >
            <div className="flex items-center space-x-2">
              {option.icon && <span>{option.icon}</span>}
              <span>{optionLabel}</span>
            </div>
          </label>
          
          {optionDescription && (
            <p className={cn(
              'text-gray-600 mt-1',
              currentSizeClasses.description,
              isDisabled && 'text-gray-400'
            )}>
              {optionDescription}
            </p>
          )}
        </div>
      </div>
    )
  }, [
    internalValue,
    disabled,
    getDisplayText,
    currentVariantClasses,
    currentSizeClasses,
    handleOptionChange
  ])
  
  // Render category group
  const renderCategoryGroup = useCallback((category: CheckboxCategory, categoryOptions: CheckboxOption[]) => {
    const categoryState = getCategoryState(categoryOptions)
    const categoryLabel = getDisplayText(
      undefined,
      category.labelEs,
      category.labelEn,
      category.id
    )
    const categoryDescription = getDisplayText(
      category.description,
      category.descriptionEs,
      category.descriptionEn,
      ''
    )
    
    return (
      <div key={category.id} className={currentVariantClasses.group}>
        {/* Category Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            {category.icon && <span>{category.icon}</span>}
            <h3 className={cn('font-semibold', currentSizeClasses.text)}>
              {categoryLabel}
            </h3>
            <span className={cn('text-gray-500', currentSizeClasses.description)}>
              ({categoryOptions.filter(opt => internalValue.includes(opt.value)).length}/{categoryOptions.filter(opt => !opt.disabled).length})
            </span>
          </div>
          
          {/* Category Select All */}
          {showSelectAll && categoryOptions.length > 1 && (
            <button
              type="button"
              onClick={() => handleSelectAllCategory(category.id, categoryOptions)}
              disabled={disabled}
              className={cn(
                'text-primary-600 hover:text-primary-700 font-medium',
                currentSizeClasses.description,
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              {categoryState === 'all' 
                ? (locale === 'es' ? 'Deseleccionar todos' : 'Deselect all')
                : selectAllLabel
              }
            </button>
          )}
        </div>
        
        {/* Category Description */}
        {categoryDescription && (
          <p className={cn('text-gray-600 mb-3', currentSizeClasses.description)}>
            {categoryDescription}
          </p>
        )}
        
        {/* Category Options */}
        <div className={cn(
          layout === 'grid' ? 'grid grid-cols-1 gap-2' : 'space-y-2'
        )}>
          {categoryOptions.map(renderCheckbox)}
        </div>
      </div>
    )
  }, [
    getCategoryState,
    getDisplayText,
    currentVariantClasses,
    currentSizeClasses,
    internalValue,
    showSelectAll,
    handleSelectAllCategory,
    disabled,
    selectAllLabel,
    locale,
    layout,
    renderCheckbox
  ])
  
  return (
    <div ref={ref} className="birhaus-checkbox-group">
      {/* Label */}
      {displayLabel && (
        <div className="mb-3">
          <h2 className={cn('font-semibold', currentSizeClasses.text)}>
            {displayLabel}
            {required && <span className="text-red-500 ml-1">*</span>}
          </h2>
          
          {displayDescription && (
            <p className={cn('text-gray-600 mt-1', currentSizeClasses.description)}>
              {displayDescription}
            </p>
          )}
        </div>
      )}
      
      {/* Global Select All */}
      {showSelectAll && !showCategories && options.length > 1 && (
        <div className="mb-4">
          <button
            type="button"
            onClick={handleSelectAll}
            disabled={disabled}
            className={cn(
              'text-primary-600 hover:text-primary-700 font-medium',
              currentSizeClasses.text,
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {internalValue.length === options.filter(opt => !opt.disabled).length
              ? (locale === 'es' ? 'Deseleccionar todos' : 'Deselect all')
              : selectAllLabel
            }
          </button>
        </div>
      )}
      
      {/* Options */}
      <div className={cn(
        currentVariantClasses.container,
        layout !== 'vertical' && safeAccess(layoutClasses, layout, layoutClasses.vertical)
      )}>
        {showCategories && allCategories.length > 0 ? (
          // Render by categories
          <>
            {allCategories.map(category => {
              const categoryOptions = groupedOptions[category.id] || []
              if (categoryOptions.length === 0) return null
              return renderCategoryGroup(category, categoryOptions)
            })}
            
            {/* Uncategorized options */}
            {groupedOptions.uncategorized && groupedOptions.uncategorized.length > 0 && (
              <div className={currentVariantClasses.group}>
                <h3 className={cn('font-semibold mb-3', currentSizeClasses.text)}>
                  {locale === 'es' ? 'Otros' : 'Other'}
                </h3>
                <div className={cn(
                  layout === 'grid' ? 'grid grid-cols-1 gap-2' : 'space-y-2'
                )}>
                  {groupedOptions.uncategorized.map(renderCheckbox)}
                </div>
              </div>
            )}
          </>
        ) : (
          // Render all options without categories
          <div className={cn(
            layout === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 gap-3' : 'space-y-3'
          )}>
            {options.map(renderCheckbox)}
          </div>
        )}
      </div>
      
      {/* Selection Summary */}
      {(minSelections || maxSelections) && (
        <div className={cn('mt-3 text-gray-600', currentSizeClasses.description)}>
          {locale === 'es' ? 'Seleccionados' : 'Selected'}: {internalValue.length}
          {maxSelections && ` / ${maxSelections}`}
          {minSelections && ` (${locale === 'es' ? 'mínimo' : 'minimum'} ${minSelections})`}
        </div>
      )}
      
      {/* Error Display */}
      {displayError && (
        <div className={cn('mt-2 text-red-600', currentSizeClasses.description)}>
          {displayError}
        </div>
      )}
    </div>
  )
})

BirhausCheckboxGroup.displayName = 'BirhausCheckboxGroup'

// Hook for enhanced checkbox group functionality
export const useBirhausCheckboxGroup = (options: CheckboxOption[], initialValues: string[] = []) => {
  const [selectedValues, setSelectedValues] = useState<string[]>(initialValues)
  
  const selectAll = useCallback(() => {
    const allValues = options.filter(opt => !opt.disabled).map(opt => opt.value)
    setSelectedValues(allValues)
  }, [options])
  
  const deselectAll = useCallback(() => {
    setSelectedValues([])
  }, [])
  
  const toggleOption = useCallback((value: string) => {
    setSelectedValues(prev => 
      prev.includes(value) 
        ? prev.filter(v => v !== value)
        : [...prev, value]
    )
  }, [])
  
  const isSelected = useCallback((value: string) => {
    return selectedValues.includes(value)
  }, [selectedValues])
  
  const getSelectedOptions = useCallback(() => {
    return options.filter(opt => selectedValues.includes(opt.value))
  }, [options, selectedValues])
  
  return {
    selectedValues,
    setSelectedValues,
    selectAll,
    deselectAll,
    toggleOption,
    isSelected,
    getSelectedOptions,
    selectedCount: selectedValues.length,
    allSelected: options.filter(opt => !opt.disabled).every(opt => selectedValues.includes(opt.value)),
    noneSelected: selectedValues.length === 0
  }
}

// IPU Admin specific checkbox options
export const ipuAdminCheckboxOptions = {
  permissions: (): CheckboxOption[] => [
    // Admin permissions
    {
      id: 'admin_users',
      value: 'admin.users',
      labelEs: 'Gestionar usuarios',
      labelEn: 'Manage users',
      descriptionEs: 'Crear, editar y eliminar usuarios del sistema',
      descriptionEn: 'Create, edit and delete system users',
      category: 'admin',
      icon: <Users className="h-4 w-4" />
    },
    {
      id: 'admin_settings',
      value: 'admin.settings',
      labelEs: 'Configuración del sistema',
      labelEn: 'System settings',
      descriptionEs: 'Modificar configuraciones globales',
      descriptionEn: 'Modify global settings',
      category: 'admin',
      icon: <Shield className="h-4 w-4" />
    },
    
    // Financial permissions
    {
      id: 'financial_donations',
      value: 'financial.donations',
      labelEs: 'Gestionar donaciones',
      labelEn: 'Manage donations',
      descriptionEs: 'Registrar y modificar donaciones',
      descriptionEn: 'Register and modify donations',
      category: 'financial'
    },
    {
      id: 'financial_reports',
      value: 'financial.reports',
      labelEs: 'Reportes financieros',
      labelEn: 'Financial reports',
      descriptionEs: 'Ver y generar reportes financieros',
      descriptionEn: 'View and generate financial reports',
      category: 'financial'
    },
    
    // Event permissions
    {
      id: 'events_create',
      value: 'events.create',
      labelEs: 'Crear eventos',
      labelEn: 'Create events',
      descriptionEs: 'Organizar nuevos eventos de la iglesia',
      descriptionEn: 'Organize new church events',
      category: 'events',
      icon: <Calendar className="h-4 w-4" />
    },
    {
      id: 'events_manage',
      value: 'events.manage',
      labelEs: 'Gestionar eventos',
      labelEn: 'Manage events',
      descriptionEs: 'Editar y administrar eventos existentes',
      descriptionEn: 'Edit and manage existing events',
      category: 'events'
    }
  ],
  
  ministries: (): CheckboxOption[] => [
    // Worship
    {
      id: 'worship_music',
      value: 'worship.music',
      labelEs: 'Música',
      labelEn: 'Music',
      category: 'worship'
    },
    {
      id: 'worship_sound',
      value: 'worship.sound',
      labelEs: 'Sonido',
      labelEn: 'Sound',
      category: 'worship'
    },
    
    // Children
    {
      id: 'children_sunday_school',
      value: 'children.sunday_school',
      labelEs: 'Escuela dominical',
      labelEn: 'Sunday school',
      category: 'children'
    },
    {
      id: 'children_nursery',
      value: 'children.nursery',
      labelEs: 'Guardería',
      labelEn: 'Nursery',
      category: 'children'
    },
    
    // Youth
    {
      id: 'youth_groups',
      value: 'youth.groups',
      labelEs: 'Grupos juveniles',
      labelEn: 'Youth groups',
      category: 'youth'
    },
    {
      id: 'youth_events',
      value: 'youth.events',
      labelEs: 'Eventos juveniles',
      labelEn: 'Youth events',
      category: 'youth'
    }
  ]
}